import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { EmptyState } from '@/components/custom/empty-state';
import { AuditoriumViewCardMessage } from './auditorium-view-card-message';
import { FiCalendar } from 'react-icons/fi';
import { useCheckIncludeProfiles } from '@/hooks/useAuthorityLevel';
import { AuditoriumViewModalFormName } from './auditorium-view-modal-form-name';
import { AuditoriumViewFormMessage } from './auditorium-view-form-message';
import { useAuditorium } from '@/hooks/useAuditorium';
import moment from 'moment';
import { t } from 'i18next';
import { Loader } from '@/components/custom/loader';
import usePermission from '@/hooks/usePermission';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSession } from '@/hooks/useSession';
import { LessonBoard, LessonBoardBody, LessonBoardHeader, LessonBoardTitle, LessonMessageDivider, LessonMessageWrapper } from '@/components/custom/lesson';
import { ChatBody } from '@/components/custom/chat/chat';
import { Button } from '@/components/ui/button';
import { PlayfulCard, PlayfulCardButton } from '@/components/custom/playful-card';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';

export function AuditoriumViewLesson({ messages, id, chats, setPage, auditorium }) {
  const playfulStudent = usePlayfulStudent();
  const playfulTeacher = usePlayfulTeacher();
  const isPlayful = playfulStudent || playfulTeacher;
  const { createMessage, lastLesson, combineMessages, hasMoreLessonComments } = useAuditorium();
  const [showModal, setShowModal] = useState(false);
  const [lessonName, setLessonName] = useState();

  const currentUser = useSession()?.session?.user;

  useEffect(() => {
    setLessonName(lastLesson?.name);
  }, [lastLesson]);

  const messageForm = useFormik({
    initialValues: {},
    onSubmit: values => {
      createMessage(id, values);
    }
  });

  function getMessageInfo(message, previousMessage, index) {
    const currentLessonId = message?.['auditorium-lesson']?.id;
    const previousLessonId = previousMessage?.['auditorium-lesson']?.id;
    const currentMessageDay = moment(message?.['created-at']).format('DD/MM/YYYY');
    const previousMessageDay = moment(previousMessage?.['created-at']).format('DD/MM/YYYY');
    const isNewLesson = previousLessonId !== undefined && previousLessonId !== currentLessonId;
    const isNewDay = currentMessageDay !== previousMessageDay;

    return {
      currentLessonId,
      previousLessonId,
      currentMessageDay,
      previousMessageDay,
      isNewLesson,
      isNewDay
    };
  }

  const isAdmin = !useCheckIncludeProfiles({ only: ['student'] });

  const shouldRenderOptions = useMemo(() => {
    if (auditorium?.['authored-by-user'] === true) {
      if (auditorium?.creator?.id !== currentUser.id) {
        return false;
      } else return true;
    }

    if (auditorium?.['authored-by-user'] === false) {
      if (isAdmin && usePermission('Interagir com monitoria', 'Monitorias')) {
        return true;
      } else return false;
    }
  }, [auditorium, isAdmin]);

  const BoardWrapper = isPlayful ? PlayfulCard : LessonBoard;

  return (
    <BoardWrapper className={isPlayful ? 'min-h-[200px] w-full grow gap-3 p-4' : undefined}>
      {isPlayful ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="mb-0 text-base font-extrabold text-neutral-900">{`${t('lessons.lesson')}: ${lessonName}`}</h2>

          {shouldRenderOptions ? (
            <PlayfulCardButton
              onClick={() => setShowModal(true)}
              className="w-auto shrink-0 px-6 py-2"
            >
              {t('auditorium.buttonLessonName')}
            </PlayfulCardButton>
          ) : null}
        </div>
      ) : (
        <LessonBoardHeader>
          <LessonBoardTitle>{`${t('lessons.lesson')}: ${lessonName}`}</LessonBoardTitle>

          {shouldRenderOptions ? (
            <Button
              variant="outline"
              size="sm"
              wide
              onClick={() => setShowModal(true)}
            >
              {t('auditorium.buttonLessonName')}
            </Button>
          ) : null}
        </LessonBoardHeader>
      )}

      <LessonBoardBody>
        {messages.length > 0 ? (
          <ChatBody id="scrollableDiv">
            <InfiniteScroll
              dataLength={combineMessages.length}
              next={() => setPage(prevPage => prevPage + 1)}
              hasMore={hasMoreLessonComments}
              inverse={true}
              scrollableTarget="scrollableDiv"
            >
              {hasMoreLessonComments && <Loader />}

              <LessonMessageWrapper>
                {combineMessages?.map((message, index) => {
                  const previousMessage = combineMessages[index - 1];
                  const messageInfo = getMessageInfo(message, previousMessage, index);
                  const isFile = message.filename && !message.body;

                  return (
                    <React.Fragment key={message.id}>
                      {messageInfo.isNewLesson && !isFile && (
                        <LessonMessageDivider className={isPlayful ? 'bg-mission-light text-mission' : undefined}>
                          <FiCalendar /> <time datetime="{moment(message?.['created-at']).format('YYYY-MM-DD')}">{moment(message?.['created-at']).format('DD/MM/YYYY')}</time>
                        </LessonMessageDivider>
                      )}
                      {!messageInfo.isNewLesson && messageInfo.isNewDay && (
                        <LessonMessageDivider className={isPlayful ? 'bg-mission-light text-mission' : undefined}>
                          <FiCalendar /> <time datetime="{moment(message?.['created-at']).format('YYYY-MM-DD')}">{moment(message?.['created-at']).format('DD/MM/YYYY')}</time>
                        </LessonMessageDivider>
                      )}
                      <AuditoriumViewCardMessage
                        key={index}
                        message={message}
                        onlyFile={!message?.['auditorium-lesson']}
                        chats={chats}
                      />
                    </React.Fragment>
                  );
                })}
              </LessonMessageWrapper>
            </InfiniteScroll>
          </ChatBody>
        ) : isPlayful ? (
          <div className="flex flex-1 items-center justify-center">
            {!isAdmin ? (
              <EmptyState
                type="content"
                title={t('emptyState.titleStudentAuditorium')}
                text={t('emptyState.textStudentAuditorium')}
              />
            ) : (
              <EmptyState
                type="content"
                title={t('emptyState.titleAdminAuditorium')}
                text={t('emptyState.textAdminAuditorium')}
              />
            )}
          </div>
        ) : (
          <>
            {!isAdmin ? (
              <EmptyState
                type="content"
                title={t('emptyState.titleStudentAuditorium')}
                text={t('emptyState.textStudentAuditorium')}
              />
            ) : (
              <EmptyState
                type="content"
                title={t('emptyState.titleAdminAuditorium')}
                text={t('emptyState.textAdminAuditorium')}
              />
            )}
          </>
        )}
      </LessonBoardBody>

      {isAdmin && usePermission('Interagir com monitoria', 'Monitorias') ? (
        <AuditoriumViewFormMessage
          isAdmin={isAdmin}
          isPlayful={isPlayful}
          currentUser={currentUser}
          auditorium={auditorium}
          messageForm={messageForm}
          lessonId={lastLesson?.id}
          createMessage={createMessage}
          auditoriumId={id}
        />
      ) : null}

      <AuditoriumViewModalFormName
        show={showModal}
        onClose={() => setShowModal(false)}
        messages={messages}
        setLessonName={setLessonName}
        id={id}
        isPlayful={isPlayful}
      />
    </BoardWrapper>
  );
}
